import axios, { AxiosError } from 'axios';

import { getApiErrorMessage } from '@/shared/api/apiError';
import {
  clearAuthSession,
  getAccessToken,
  persistSessionEndMessage,
} from '@/shared/api/authSession';
import { createApiPath } from '@/shared/api/endpoints';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://dev.api.rudalink.com';

export const SESSION_EXPIRED_MESSAGE =
  '로그인이 만료되었습니다. 다시 로그인해 주세요.';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  // 배열 파라미터는 key=1&key=2 (Spring 목록 바인딩). 기본값 key[]=1 은 쓰지 않는다
  paramsSerializer: { indexes: null },
});

let isEndingSession = false;

/** 세션 만료 시 로그인 이동 — 테스트에서 교체 가능 */
let redirectToLogin = () => {
  window.location.href = '/';
};

export const __setRedirectToLoginForTests = (fn: () => void) => {
  redirectToLogin = fn;
};

export const __resetSessionStateForTests = () => {
  isEndingSession = false;
  redirectToLogin = () => {
    window.location.href = '/';
  };
};

/** 로그인 실패 401은 세션 만료가 아니다. 로그아웃 401은 authStore가 정리한다. */
const isAuthEntryEndpoint = (url?: string) =>
  !!url &&
  (url.includes(createApiPath.auth.login()) ||
    url.includes(createApiPath.auth.logout()));

/**
 * 401 = 세션 종료. refresh API가 없으므로 재시도 없이 바로 재로그인시킨다.
 * 동시에 여러 요청이 401이어도 한 번만 이동한다.
 */
const endSession = (error: AxiosError) => {
  if (isEndingSession) return;
  isEndingSession = true;
  persistSessionEndMessage(getApiErrorMessage(error, SESSION_EXPIRED_MESSAGE));
  clearAuthSession();
  redirectToLogin();
};

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (
      error.response?.status === 401 &&
      !isAuthEntryEndpoint(error.config?.url)
    ) {
      endSession(error);
    }
    return Promise.reject(error);
  },
);

/**
 * GET 쿼리에서 빈 값(undefined·null·'')을 뺀다.
 * 서버는 빈 문자열을 null로 정규화하지만, 시/군/구 @Pattern 검증 등에 걸리지 않게 아예 보내지 않는다.
 */
export const toQueryParams = (
  params: object,
): Record<string, string | number | boolean | Array<string | number>> =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0),
    ),
  );
